/**
 * Client-side half of the AI Agent starter chips.
 *
 * The catalogue (labels, messages, where each chip shows, selection caps) is
 * server-side: filespin-mcp-server internal/prompts/starters.json, served by
 * GET /api/v1/chat/suggestions. The server cannot cheaply know the user's
 * role, addons or schemas, so each starter carries `requires` tags and the UI
 * evaluates them here, then caps the count.
 *
 * Pure data, no React, so it stays unit-testable.
 */
import type { Starter, StarterSurface } from '../types';

/** Where a nudge row is mounted. The mounts keep these names; the server
 *  calls them by their surface. */
export type NudgeSurface = 'selection' | 'asset-detail' | 'post-upload';

export const NUDGE_SURFACE: Record<NudgeSurface, StarterSurface> = {
  selection: 'nudge.selection',
  'asset-detail': 'nudge.asset',
  'post-upload': 'nudge.post-upload',
};

/** Everything a `requires` tag may inspect, assembled by useStarterGates. */
export type StarterGateContext = {
  isUser: boolean;
  /** The account has at least one active asset schema, so metadata writes
   *  have fields to target (update_metadata validates against the schema). */
  hasActiveSchema: boolean;
  removeBackgroundAvailable: boolean;
  printExportAvailable: boolean;
  /** DELIVERY_INSIGHTS — the same addon that gates "Get campaign links" in
   *  the With Selected menu. */
  campaignLinksAvailable: boolean;
};

const GATES: Record<string, (ctx: StarterGateContext) => boolean> = {
  'not_role:USER': (c) => !c.isUser,
  'schema:active': (c) => c.hasActiveSchema,
  'addon:REMOVE_BACKGROUND': (c) => c.removeBackgroundAvailable,
  'addon:PRINT_EXPORT': (c) => c.printExportAvailable,
  'addon:DELIVERY_INSIGHTS': (c) => c.campaignLinksAvailable,
};

/** True when every tag passes. An unknown tag (a newer server than this UI)
 *  fails closed: better a missing chip than one that dies on click. */
export function passesRequires(
  requires: string[] | undefined,
  ctx: StarterGateContext,
): boolean {
  return (requires ?? []).every((tag) => GATES[tag]?.(ctx) ?? false);
}

/** Gate, then cap. The server sends starters sorted by priority and uncapped
 *  for exactly this order of operations. */
export function pickStarters(
  starters: Starter[] | undefined,
  ctx: StarterGateContext,
  limit: number,
): Starter[] {
  return (starters ?? []).filter((s) => passesRequires(s.requires, ctx)).slice(0, limit);
}

/** Opens the panel focused without pre-filling anything — the overflow story
 *  for users whose intent isn't in the list. Rendered last, always. */
export const ASK_OPEN_ID = 'ask_open';
